/**
 * Same-origin realtime bus for multi-tab / multi-window lobby traffic.
 *
 * PeerBasket is rate-limited per IP and WebRTC often never opens after hard
 * refresh. Two windows on dechess.org (or localhost) can still exchange chat
 * and challenges instantly via BroadcastChannel + localStorage.
 */

import { lobbyDebug } from './lobbyDebug.js';

export const LOBBY_LOCAL_BUS_CHANNEL = 'dechess-lobby-bus-v1';
const STORAGE_KEY = 'dechess-lobby-bus-v1:pulse';

function canUseBroadcastChannel() {
  return typeof BroadcastChannel !== 'undefined';
}

function canUseLocalStorage() {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

export function publishLocalLobbyEnvelope(envelope) {
  if (!envelope?.type) {
    return { published: false };
  }
  const message = {
    ...envelope,
    sentAt: Number(envelope.sentAt) || Date.now(),
    via: 'local-bus'
  };
  let published = false;

  if (canUseBroadcastChannel()) {
    try {
      const channel = new BroadcastChannel(LOBBY_LOCAL_BUS_CHANNEL);
      channel.postMessage(message);
      channel.close();
      published = true;
    } catch {
      // Fall through to storage.
    }
  }

  if (canUseLocalStorage()) {
    try {
      // storage events fire in *other* documents on the same origin.
      localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
      localStorage.removeItem(STORAGE_KEY);
      published = true;
    } catch {
      // Ignore quota / private mode.
    }
  }

  lobbyDebug('local-bus-publish', {
    type: message.type,
    published,
    senderId: message.senderId
  });
  return { published, queued: published };
}

export function subscribeLocalLobbyEnvelope(onMessage) {
  if (typeof onMessage !== 'function') {
    return () => undefined;
  }

  const deliver = (raw) => {
    if (!raw || typeof raw !== 'object' || !raw.type) {
      return;
    }
    lobbyDebug('local-bus-receive', {
      type: raw.type,
      senderId: raw.senderId
    });
    onMessage(raw);
  };

  let channel = null;
  if (canUseBroadcastChannel()) {
    try {
      channel = new BroadcastChannel(LOBBY_LOCAL_BUS_CHANNEL);
      channel.onmessage = (event) => deliver(event.data);
    } catch {
      channel = null;
    }
  }

  const onStorage = (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) {
      return;
    }
    try {
      deliver(JSON.parse(event.newValue));
    } catch {
      // Ignore malformed pulses.
    }
  };

  if (canUseLocalStorage() && typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }

  return () => {
    if (channel) {
      try {
        channel.close();
      } catch {
        // Ignore.
      }
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}
